import { ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatAnchor } from '@angular/material/button';
import { ButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { AuthService } from '@libs/shared/auth';
import { CapacitorService, DialogService, EnvironmentService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import {
  footerAccountDeleteNavItem,
  footerDpgConditionsNavItem,
  footerFaqNavItem,
  footerGeneralTermsAndConditionsNavItem,
  footerHolidaysNavItem,
  footerImprintNavItem,
  footerPrivacyNavItem,
  footerTosNavItem,
  NavItemModel,
  NavModel,
  srmNavItem,
} from '@libs/shared/models';
import { FormatAbsolutePipe, LinkDirective, NavLinkDirective } from '@libs/shared/ui';
import { ProductAvailabilityDialogFormComponent } from '@libs/shop/components';
import { DateTime } from 'luxon';
import { from } from 'rxjs';

@Component({
  selector: 'ui-cep-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAnchor,
    ButtonComponent,
    NavLinkDirective,
    IconComponent,
    LinkDirective,
    FormatAbsolutePipe,
  ],
})
export class FooterComponent {
  public authorized: InputSignal<boolean> = input(false);

  public isBmiCustomer: Signal<boolean> = computed(() => Boolean(this.authService.currentCustomer()?.customerCategorizing?.bmi));
  public appVersion: Signal<string | undefined> = toSignal(from(this.capacitorService.loadAppVersion()));

  public isCapacitorApp: Signal<boolean> = computed(() => this.capacitorService.isCapacitorApp());
  public deliveryTrackingLink: Signal<string> = computed(() => this.env.links().deliveryTracking);
  public canUseSrm: Signal<boolean> = this.capabilityService.isAvailable('services.srm');

  public currentYear: number = DateTime.now().year;

  public navServices: Signal<NavModel> = computed(() => {
    const srmItems: NavItemModel[] = this.canUseSrm() ? [srmNavItem] : [];

    return {
      title: 'Services',
      description: 'Alle Services',
      children: [
        {
          label: 'Lieferverfolgung',
          url: this.deliveryTrackingLink(),
          target: '_blank',
        },
        ...srmItems,
        footerFaqNavItem,
        footerHolidaysNavItem,
      ],
    };
  });

  public navContacts: Signal<NavModel> = computed(() => {
    return {
      title: 'Digital Support',
      description: 'Alle Kontaktmöglichkeiten',
      children: [
        {
          label: 'Tel: 02234/ 9314-444',
          url: 'tel:+4922349314444',
        },
        {
          label: 'Kontakt',
          url: 'mailto:digitalsupport@lekkerland.de',
        },
      ],
    };
  });

  public navFeatures: Signal<NavModel> = computed(() => {
    return {
      title: 'Features',
      description: 'Alle Features',
      children: [
        {
          label: 'Kundennummernwechsel',
          url: '/switch-customer',
        },
        {
          label: 'Warenverfügbarkeit',
          clickable: true,
        },
      ],
    };
  });

  public navLekkerlandApp: Signal<NavModel> = computed(() => {
    return {
      title: 'Lekkerland24 Apps:',
      children: [
        {
          image: {
            src: './assets/img/footer/LL24_App_MVi.svg',
            alt: 'QR-Code',
          },
        },
        {
          url: 'https://apps.apple.com/de/app/lekkerland24/id6450741880?itscg=30200&itsct=apps_box_badge&mttnsubad=6450741880',
          target: '_blank',
          image: {
            src: 'https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/de-de?releaseDate=1727136000',
            alt: 'Download on the App Store',
          },
        },
        {
          url: 'https://play.google.com/store/apps/details?id=com.lekkerland.cep&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1',
          target: '_blank',
          image: {
            src: 'https://play.google.com/intl/en_us/badges/static/images/badges/de_badge_web_generic.png',
            alt: 'Jetzt bei Google Play',
          },
        },
      ],
    };
  });

  public navLegal: Signal<NavModel> = computed(() => {
    const authorizedItems: NavItemModel[] = this.authorized() ? [footerAccountDeleteNavItem] : [];

    return {
      title: '',
      children: [
        footerImprintNavItem,
        footerGeneralTermsAndConditionsNavItem,
        footerPrivacyNavItem,
        {
          label: 'Privatsphäre-Einstellungen',
          type: 'privacy-settings',
        },
        footerTosNavItem,
        footerDpgConditionsNavItem,
        ...authorizedItems,
      ],
    };
  });

  public constructor(
    private readonly env: EnvironmentService,
    private readonly authService: AuthService,
    private readonly capacitorService: CapacitorService,
    private readonly dialogService: DialogService,
    private readonly capabilityService: CapabilityService,
  ) {
  }

  public openProductAvailabilityInfo(): void {
    this.dialogService.open(ProductAvailabilityDialogFormComponent);
  }

  public openNewsletter(): void {
    window.open(this.env.links().whatsAppNewsletter, '_blank');
  }
}
